import React from 'react';
import { Box } from '@mui/material';
import { Uniform } from '../../../../components/uniform/uniform';
import { UniformStepContainer } from '../../../../components/uniform/uniform-step-container';
import { UniformStep } from '../../../../components/uniform/uniform-step';
import { CourseEnrollmentTitlezone, getCourseEnrollmentTitleMode } from './course-enrollment-titlezone';
import { CourseEnrollmentLaunchbox } from './course-enrollment-launchbox';
import { Accordion, AccordionSummary, AccordionDetails } from '../../../../components/accordion';
import { Row } from '../../../../components/row';
import { Cell } from '../../../../components/cell';
import { SingleSelect } from '../../../../components/single-select';
import { CourseDto, CourseEnrollmentActivityDto, ReportType } from '../../../../api-service/clients';
import { CourseEnrollment, CourseEnrollmentErrors } from './course-enrollment';
import { formatDate, formatPeriod } from '../../../../utilities';
import { ReadonlyInput } from '../../../../components/readonly-input';
import { DatePicker, DatePickerValue } from '../../../../components/date-picker';
import { Checkbox } from '../../../../components/checkbox';
import { FormControlLabel } from '../../../../components/form-control-label';
import { FormControl } from '../../../../components/form-control';
import { YesNo, YesNoComponent } from '../../../../components/radio-group/yes-no-component';
import { Typography } from '../../../../components/typography';
import { CourseSelector } from '../components/course-selector';
import { FormHelperText } from '../../../../components/form-helper-text/form-helper-text';
import { CitizenCaseLookup } from '../../../../components/lookups/citizen-case-lookup/citizen-case-lookup';

interface ICourseEnrollmentComponentProps {
  isEditmode: boolean;
  isLoading: boolean;
  onSaveCourseEnrollmentClick: () => void;
  onCancelCourseEnrollment: () => void;
  onValidate: () => boolean;
  onBooleanChange: <K extends keyof CourseEnrollment>(name: K, value?: boolean) => void;
  onDateChange: <K extends keyof CourseEnrollment>(name: K, value?: DatePickerValue) => void;
  onReportTypeChange: (value?: ReportType) => void;
  onCourseChange: (course?: CourseDto) => void;
  courseEnrollment: CourseEnrollment;
  errors: CourseEnrollmentErrors;
  activities: CourseEnrollmentActivityDto[];
  onActivityChange: (activity?: CourseEnrollmentActivityDto) => void;
  onCaseChange: (value: string | undefined) => void;
  citizenId: string | undefined;
  activityId?: string;
  offerId?: string;
  isInActivityCreateMode: boolean;
}

export function CourseEnrollmentComponent(props: ICourseEnrollmentComponentProps) {
  const {
    isEditmode,
    onSaveCourseEnrollmentClick,
    isLoading,
    onValidate,
    onCourseChange,
    courseEnrollment,
    onCancelCourseEnrollment,
    onBooleanChange,
    onDateChange,
    onReportTypeChange,
    errors,
    activities,
    onActivityChange,
    onCaseChange,
    citizenId,
    activityId,
    offerId,
    isInActivityCreateMode,
  } = props;

  const {
    courseId,
    beginAt,
    endAt,
    desiredReportType,
    createFollowUpAdvisOnMissingAttendance,
    createNegativeEventOnMissingAttendance,
    createAdvisIfNotOnTime,
    followDfdgActivityPeriod,
    hasDutyToWork,
    isActive,
  } = courseEnrollment;

  const isReadOnly = !isActive;

  return (
    <Uniform
      titleZone={<CourseEnrollmentTitlezone mode={getCourseEnrollmentTitleMode(isEditmode, isActive)} />}
      launchbox={
        <CourseEnrollmentLaunchbox
          isLoading={isLoading}
          onSave={onSaveCourseEnrollmentClick}
          onValidate={onValidate}
          onCancelCourseEnrollment={onCancelCourseEnrollment}
          isReadOnly={isReadOnly}
        />
      }
      isEditMode={isEditmode}
      showButtons={!isEditmode}
      onOk={onSaveCourseEnrollmentClick}
      onCancel={() => window.close()}
      okButtonIsLoading={isLoading}
      showSpinnerOnNextAndComplete={isLoading}
    >
      <UniformStep stepName="Generelt" validate={onValidate}>
        <UniformStepContainer>
          <Accordion defaultExpanded>
            <AccordionSummary>Hold og periode</AccordionSummary>
            <AccordionDetails>
              <Box mt={1}>
                <Row>
                  <Cell size="full">
                    <CourseSelector
                      activityId={isInActivityCreateMode ? activityId : undefined}
                      offerId={isInActivityCreateMode ? offerId : undefined}
                      isEditmode={isEditmode}
                      courseId={courseId}
                      onCourseChange={onCourseChange}
                      error={!!errors.courseId}
                      errorText={errors.courseId}
                    />
                  </Cell>
                </Row>
                {(courseId || courseEnrollment.activityId) && (
                  <Row>
                    <Cell size="half">
                      {!isReadOnly && (
                        <SingleSelect<CourseEnrollmentActivityDto>
                          label="Aktivitet"
                          variant="outlined"
                          getOptionLabel={x => x.title || ''}
                          onChange={onActivityChange}
                          options={activities}
                          renderOption={(optionProps: object, option: CourseEnrollmentActivityDto) => (
                            <li {...optionProps} key={option.activityId}>
                              {getActivityOptionLabel(option)}
                            </li>
                          )}
                          value={activities.find(x => x.activityId === courseEnrollment.activityId)}
                          error={!!errors.desiredReportType || !!errors.activityInvalidEndDate || !!errors.activityNotOnOffer}
                        />
                      )}
                      {isReadOnly && <ReadonlyInput label="Aktivitet" value={activities.find(x => x.activityId === courseEnrollment.activityId)?.title} />}
                      {errors.activityNotOnOffer && <FormHelperText sx={{ color: 'error.main' }}>{errors.activityNotOnOffer}</FormHelperText>}
                      {errors.activityInvalidEndDate && <FormHelperText sx={{ color: 'error.main' }}>{errors.activityInvalidEndDate}</FormHelperText>}
                      {errors.desiredReportType && <FormHelperText sx={{ color: 'error.main' }}>{errors.desiredReportType}</FormHelperText>}
                    </Cell>
                    <Cell size="half">
                      <CitizenCaseLookup
                        caseId={courseEnrollment.caseId}
                        variant="outlined"
                        citizenId={citizenId}
                        onChange={onCaseChange}
                        readOnly={isReadOnly || courseEnrollment.activityId != null}
                        required
                        error={!!errors.caseId}
                        errorText={errors.caseId || ''}
                        autoSelectPrimaryCase
                      />
                    </Cell>
                  </Row>
                )}
                {courseId && (
                  <>
                    <Row>
                      <Cell size="full">
                        <FormControl sx={{ marginLeft: '3px' }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!courseEnrollment.activityId && (followDfdgActivityPeriod ?? false)}
                                disabled={isReadOnly || !courseEnrollment.activityId}
                                onChange={(_e, checked) => onBooleanChange('followDfdgActivityPeriod', checked)}
                              />
                            }
                            label="Følg aktivitetens periode"
                          />
                        </FormControl>
                      </Cell>
                    </Row>
                    <Row>
                      <Cell size="half">
                        {!isReadOnly && (
                          <DatePicker
                            fullWidth
                            variant="outlined"
                            label="Startdato"
                            onChange={value => onDateChange('beginAt', value)}
                            value={beginAt}
                            required={!followDfdgActivityPeriod}
                            error={!!errors.beginAt}
                            errorText={errors.beginAt}
                            disabled={followDfdgActivityPeriod}
                          />
                        )}
                        {isReadOnly && <ReadonlyInput label="Startdato" value={formatDate(beginAt)} />}
                      </Cell>
                      <Cell size="half">
                        {!isReadOnly && (
                          <DatePicker
                            fullWidth
                            variant="outlined"
                            label="Slutdato"
                            onChange={value => onDateChange('endAt', value)}
                            value={endAt}
                            required={!followDfdgActivityPeriod}
                            error={!!errors.endAt}
                            errorText={errors.endAt}
                            disabled={followDfdgActivityPeriod}
                          />
                        )}
                        {isReadOnly && <ReadonlyInput label="Slutdato" value={formatDate(endAt)} />}
                      </Cell>
                    </Row>
                  </>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
          {courseId && (
            <Accordion defaultExpanded>
              <AccordionSummary>Indstillinger for fremmødeforløb</AccordionSummary>
              <AccordionDetails>
                <Box mt={1}>
                  <Row>
                    <Cell size="full">
                      <Typography variant="subtitle2">Arbejdspligt</Typography>
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      <YesNoComponent
                        header="Indgår i borgers arbejdspligt"
                        name="hasDutyToWork"
                        value={hasDutyToWork ? YesNo.Yes : YesNo.No}
                        onChange={value => onBooleanChange('hasDutyToWork', value === YesNo.Yes)}
                        hideNotEvaluated
                        readOnly={isReadOnly}
                      />
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      <Box mt={2}>
                        <Typography variant="subtitle2">Hændelser og adviser</Typography>
                      </Box>
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      <YesNoComponent
                        header="Opret negativ hændelse ved manglende fremmøde"
                        name="createNegativeEventOnMissingAttendance"
                        value={createNegativeEventOnMissingAttendance ? YesNo.Yes : YesNo.No}
                        onChange={value => onBooleanChange('createNegativeEventOnMissingAttendance', value === YesNo.Yes)}
                        hideNotEvaluated
                        readOnly={isReadOnly}
                      />
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      <YesNoComponent
                        header="Opret advis ved manglende fremmøde"
                        name="createFollowUpAdvisOnMissingAttendance"
                        value={createFollowUpAdvisOnMissingAttendance ? YesNo.Yes : YesNo.No}
                        onChange={value => onBooleanChange('createFollowUpAdvisOnMissingAttendance', value === YesNo.Yes)}
                        hideNotEvaluated
                        readOnly={isReadOnly}
                      />
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      <YesNoComponent
                        header="Opret advis ved for sent fremmøde"
                        name="createAdvisIfNotOnTime"
                        value={createAdvisIfNotOnTime ? YesNo.Yes : YesNo.No}
                        onChange={value => onBooleanChange('createAdvisIfNotOnTime', value === YesNo.Yes)}
                        hideNotEvaluated
                        readOnly={isReadOnly}
                      />
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      <Box mt={2}>
                        <Typography variant="subtitle2">Journalisering</Typography>
                      </Box>
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="half">
                      {!isReadOnly && (
                        <SingleSelect<ReportType>
                          label="Frekvens for fremmøderapport"
                          variant="outlined"
                          required
                          getOptionLabel={x => getReportTypeDisplay(x)}
                          onChange={onReportTypeChange}
                          options={[ReportType.Never, ReportType.Weekly, ReportType.Monthly]}
                          renderOption={(optionProps: object, option: ReportType) => (
                            <li {...optionProps} key={option}>
                              {getReportTypeDisplay(option)}
                            </li>
                          )}
                          value={desiredReportType}
                          error={!!errors.desiredReportType}
                          errorText={errors.desiredReportType}
                        />
                      )}
                      {isReadOnly && desiredReportType !== undefined && <ReadonlyInput label="Frekvens for fremmøderapport" value={getReportTypeDisplay(desiredReportType)} />}
                    </Cell>
                  </Row>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </UniformStepContainer>
      </UniformStep>
    </Uniform>
  );
}

function getActivityOptionLabel(activity: CourseEnrollmentActivityDto) {
  const period = activity.startDate || activity.endDate ? `(${formatPeriod(activity.startDate, activity.endDate)})` : null;
  return [activity.title, period].filter(Boolean).join(' ');
}

function getReportTypeDisplay(value: ReportType) {
  switch (value) {
    case ReportType.Never:
      return 'Aldrig';
    case ReportType.Weekly:
      return 'Ugentlig';
    case ReportType.Monthly:
      return 'Månedlig';
    default:
      return 'Aldrig';
  }
}
